/* D2 — sélecteur de System persistant (topbar). Change UNIQUEMENT le contexte
   de navigation ; ne contourne jamais systems_enabled, rôle ou ACL (le backend
   fail-close reste la source de vérité). */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, Hexagon, Lock } from "lucide-react";
import { SYSTEM_DEFS, systemForPath, systemLanding, useSystems } from "@/lib/systems";

export const SystemSelector = () => {
  const { systems, depth } = useSystems();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState({ top: 56, left: 200 });

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (!e.target.closest?.("[data-system-selector]")) setOpen(false);
    };
    const onScroll = () => setOpen(false);
    window.addEventListener("click", onClick);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("click", onClick);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open]);

  const current = systemForPath(location.pathname, location.search);
  const currentDef = SYSTEM_DEFS.find((d) => d.id === current);

  const go = (def, enabled) => {
    setOpen(false);
    navigate(enabled ? (systemLanding(def, depth) || def.landing) : "/app/hub");
  };

  return (
    <div className="relative hidden md:block flex-shrink-0" data-system-selector data-testid="topbar-system-selector">
      <button
        onClick={(e) => {
          if (!open) {
            const r = e.currentTarget.getBoundingClientRect();
            setAnchor({ top: Math.round(r.bottom + 6), left: Math.round(r.left) });
          }
          setOpen(!open);
        }}
        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md border transition-colors whitespace-nowrap ${
          open
            ? "border-[#378ADD]/60 bg-[#378ADD]/15 text-white"
            : "border-white/10 bg-white/[0.03] text-zinc-300 hover:border-[#378ADD]/40 hover:bg-[#378ADD]/10 hover:text-white"
        }`}
        title="Changer de System"
        data-testid="system-selector-btn"
      >
        <Hexagon className="w-3 h-3 text-[#378ADD] shrink-0" />
        <span className="text-xs font-medium text-white">{currentDef?.label || "Hub NOAM"}</span>
        {currentDef && !systems.includes(currentDef.id) && (
          <Lock className="w-3 h-3 text-amber-400" data-testid="system-selector-locked" />
        )}
        <ChevronDown className={`w-3 h-3 text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          className="noam-card py-1 min-w-[230px]"
          style={{ position: "fixed", top: anchor.top, left: anchor.left, zIndex: 9999 }}
          data-testid="system-selector-menu"
        >
          <button
            onClick={() => go({ landing: "/app/hub" }, true)}
            className={`w-full text-left px-3 py-2 text-xs hover:bg-white/5 flex items-center gap-2 ${!current ? "text-[#378ADD] bg-[#378ADD]/10" : "text-zinc-300"}`}
            data-testid="system-option-hub"
          >
            <Hexagon className="w-3.5 h-3.5" /> Hub NOAM
          </button>
          <div className="h-px my-1 bg-white/5" />
          {SYSTEM_DEFS.map((def) => {
            const enabled = systems.includes(def.id);
            return (
              <button
                key={def.id}
                onClick={() => go(def, enabled)}
                className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-white/5 ${
                  current === def.id ? "text-[#378ADD] bg-[#378ADD]/10" : enabled ? "text-zinc-300" : "text-zinc-500"
                }`}
                data-testid={`system-option-${def.id}`}
              >
                <def.icon className="w-3.5 h-3.5" style={{ color: enabled ? def.accent : undefined }} />
                <span className="flex-1">{def.label}</span>
                {!enabled && (
                  <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider text-zinc-500">
                    <Lock className="w-3 h-3" /> Découvrir
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SystemSelector;
